import { Controller } from "@hotwired/stimulus"

// History time-travel lightbox. Opened from a reign chain, it shows a
// challenge's champion generations as a "time corridor": the focused era sits
// large and sharp in the center while the previous and next eras peek in from
// the edges, ghosted (scaled down, dimmed, blurred). Travelling with the arrow
// keys (or the on-screen chevrons / era ribbon / a ghosted neighbor) slides the
// whole corridor so you feel yourself moving through time, not just flipping a
// before/after. Video eras play only while focused; an editorial note renders
// as a speech bubble on the era it belongs to.
//
// This is deliberately distinct from the canonical lightbox: same media, a
// different intent (the passage of time).

interface Era {
  model: string
  modelSlug?: string | null
  date: string
  end?: string | null
  duration?: string | null
  ongoing?: boolean
  overtook?: string | null
  image?: string | null
  video?: string | null
  poster?: string | null
  note?: string | null
}

const EASE = "cubic-bezier(0.16, 1, 0.3, 1)"

export default class extends Controller {
  static values = { eras: Array, prompt: String, mode: String, inputs: Array, scale: Number }

  declare erasValue: Era[]
  declare promptValue: string
  declare modeValue: string
  declare inputsValue: string[]
  declare scaleValue: number

  private overlay: HTMLElement | null = null
  private track!: HTMLElement
  private panels: HTMLElement[] = []
  private captionEl!: HTMLElement
  private ribbonEl!: HTMLElement
  private index = 0
  private keyHandler = (e: KeyboardEvent) => this.onKey(e)

  open(event: Event & { params?: { index?: number } }) {
    const raw =
      event.params?.index ??
      (event.currentTarget as HTMLElement)?.dataset?.historyLightboxIndexParam
    const idx = Number.parseInt(String(raw ?? 0), 10)
    if (!Array.isArray(this.erasValue) || this.erasValue.length === 0) return
    event.preventDefault?.()
    if (!this.overlay) this.build()
    this.show(Number.isFinite(idx) ? idx : 0)
  }

  disconnect() {
    this.teardown()
    this.overlay?.remove()
    this.overlay = null
  }

  // ---- build (once) -------------------------------------------------------

  private build() {
    const root = document.createElement("div")
    root.className = "bm-tt"
    root.setAttribute("role", "dialog")
    root.setAttribute("aria-modal", "true")
    root.setAttribute("aria-label", "Time travel through the champions")
    root.hidden = true
    root.innerHTML = `
      <div class="bm-tt__scrim" data-tt="scrim"></div>
      <button type="button" class="bm-tt__close" data-tt="close" aria-label="Close">${ICON_X}</button>
      <div class="bm-tt__prompt" data-tt="prompt"></div>
      <button type="button" class="bm-tt__nav bm-tt__nav--prev" data-tt="prev" aria-label="Earlier">${ICON_LEFT}</button>
      <div class="bm-tt__stage">
        <div class="bm-tt__track" data-tt="track"></div>
      </div>
      <button type="button" class="bm-tt__nav bm-tt__nav--next" data-tt="next" aria-label="Later">${ICON_RIGHT}</button>
      <div class="bm-tt__source" data-tt="source" hidden></div>
      <div class="bm-tt__caption" data-tt="caption"></div>
      <div class="bm-tt__ribbon" data-tt="ribbon"></div>
    `
    document.body.appendChild(root)
    this.overlay = root
    this.track = root.querySelector('[data-tt="track"]') as HTMLElement
    this.captionEl = root.querySelector('[data-tt="caption"]') as HTMLElement
    this.ribbonEl = root.querySelector('[data-tt="ribbon"]') as HTMLElement

    const prompt = root.querySelector('[data-tt="prompt"]') as HTMLElement
    if (this.promptValue) {
      prompt.textContent = this.promptValue
    } else {
      prompt.remove()
    }

    root.querySelector('[data-tt="scrim"]')?.addEventListener("click", () => this.close())
    root.querySelector('[data-tt="close"]')?.addEventListener("click", () => this.close())
    root.querySelector('[data-tt="prev"]')?.addEventListener("click", () => this.go(-1))
    root.querySelector('[data-tt="next"]')?.addEventListener("click", () => this.go(1))

    this.buildPanels()
    this.buildRibbon()
    this.renderSource()
    this.injectStyles()
  }

  // Pinned source: for image-editing the input image(s) stay fixed while the
  // champion edits travel; for upscaling the original stays fixed and the
  // focused upscale gets a hover loupe (see buildPanels). Other modes: no source.
  private renderSource() {
    const el = this.overlay!.querySelector('[data-tt="source"]') as HTMLElement
    const inputs = Array.isArray(this.inputsValue) ? this.inputsValue : []
    const isEdit = this.modeValue === "image_editing"
    const isUpscale = this.modeValue === "image_upscaling"
    if ((!isEdit && !isUpscale) || inputs.length === 0) {
      el.hidden = true
      return
    }
    el.hidden = false
    const label = isUpscale ? "Original" : inputs.length > 1 ? "Inputs" : "Input"
    el.innerHTML = `
      <span class="bm-tt__source-label">${label} <span>tap to enlarge</span></span>
      <div class="bm-tt__source-imgs">${inputs
        .map(
          (u) =>
            `<button type="button" class="bm-tt__source-thumb" data-src-full="${escapeAttr(
              u
            )}" aria-label="Enlarge ${label.toLowerCase()}"><img src="${escapeAttr(
              u
            )}" alt="${label}" loading="lazy"></button>`
        )
        .join("")}</div>`

    // The source/input image is itself worth inspecting (it's what every era
    // edits or upscales). Clicking a thumb opens it enlarged in a lightweight
    // zoom layer that sits ABOVE the corridor without disturbing era state, so
    // arrow-key / chevron navigation stays intact behind it.
    el.querySelectorAll<HTMLButtonElement>(".bm-tt__source-thumb").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        const full = btn.dataset.srcFull
        if (full) this.openSourceZoom(full, label)
      })
    })
  }

  // Enlarged view of a pinned source/input image. Scoped to its own scrim +
  // figure inside the overlay; Escape or a click on the scrim closes only the
  // zoom (era navigation is untouched). Built lazily on first use.
  private openSourceZoom(url: string, label: string) {
    if (!this.overlay) return
    let zoom = this.overlay.querySelector('[data-tt="zoom"]') as HTMLElement | null
    if (!zoom) {
      zoom = document.createElement("div")
      zoom.className = "bm-tt__zoom"
      zoom.dataset.tt = "zoom"
      zoom.hidden = true
      zoom.innerHTML = `
        <div class="bm-tt__zoom-scrim" data-tt="zoom-scrim"></div>
        <button type="button" class="bm-tt__zoom-close" data-tt="zoom-close" aria-label="Close">${ICON_X}</button>
        <figure class="bm-tt__zoom-figure">
          <img data-tt="zoom-img" alt="">
          <figcaption class="bm-tt__zoom-cap" data-tt="zoom-cap"></figcaption>
        </figure>`
      this.overlay.appendChild(zoom)
      zoom
        .querySelector('[data-tt="zoom-scrim"]')
        ?.addEventListener("click", () => this.closeSourceZoom())
      zoom
        .querySelector('[data-tt="zoom-close"]')
        ?.addEventListener("click", () => this.closeSourceZoom())
    }
    const img = zoom.querySelector('[data-tt="zoom-img"]') as HTMLImageElement
    const cap = zoom.querySelector('[data-tt="zoom-cap"]') as HTMLElement
    img.src = url
    img.alt = label
    cap.textContent = label
    zoom.hidden = false
    requestAnimationFrame(() => zoom!.classList.add("is-open"))
  }

  private closeSourceZoom() {
    const zoom = this.overlay?.querySelector('[data-tt="zoom"]') as HTMLElement | null
    if (!zoom) return
    zoom.classList.remove("is-open")
    window.setTimeout(() => {
      zoom.hidden = true
    }, 200)
  }

  private buildPanels() {
    this.track.innerHTML = ""
    this.panels = this.erasValue.map((era, i) => {
      const panel = document.createElement("figure")
      panel.className = "bm-tt__panel"
      panel.dataset.index = String(i)

      const media = document.createElement("div")
      media.className = "bm-tt__media"
      if (era.video) {
        const v = document.createElement("video")
        v.muted = true
        v.loop = true
        v.playsInline = true
        v.preload = "none"
        if (era.poster) v.poster = era.poster
        v.dataset.src = era.video
        media.appendChild(v)
        const badge = document.createElement("span")
        badge.className = "bm-tt__playbadge"
        badge.innerHTML = ICON_PLAY
        media.appendChild(badge)
      } else if (era.image) {
        const img = document.createElement("img")
        img.loading = "lazy"
        img.decoding = "async"
        img.src = era.image
        img.alt = `${era.model}, ${era.date}`
        media.appendChild(img)
      }

      // A ghosted neighbor is a click target to travel one step.
      panel.addEventListener("click", () => {
        if (i !== this.index) this.jumpTo(i)
      })

      // Upscaling: the focused panel gets a hover loupe so you can inspect the
      // upscale detail against the pinned original.
      if (this.modeValue === "image_upscaling") {
        panel.classList.add("is-upscale")
        const img = media.querySelector("img") as HTMLImageElement | null
        if (img) this.attachLoupe(panel, img)
      }

      panel.appendChild(media)
      this.track.appendChild(panel)
      return panel
    })
  }

  private attachLoupe(panel: HTMLElement, img: HTMLImageElement) {
    const move = (e: MouseEvent) => {
      if (!panel.classList.contains("is-focused")) return
      const r = img.getBoundingClientRect()
      const x = clamp(((e.clientX - r.left) / r.width) * 100, 0, 100)
      const y = clamp(((e.clientY - r.top) / r.height) * 100, 0, 100)
      img.style.transition = "none"
      img.style.transformOrigin = `${x}% ${y}%`
      img.style.transform = `scale(${this.scaleValue && this.scaleValue > 1 ? Math.min(this.scaleValue, 4) : 2.4})`
    }
    const reset = () => {
      img.style.transition = ""
      img.style.transform = ""
    }
    img.addEventListener("mousemove", move)
    img.addEventListener("mouseleave", reset)
  }

  private buildRibbon() {
    this.ribbonEl.innerHTML = ""
    this.erasValue.forEach((era, i) => {
      const stop = document.createElement("button")
      stop.type = "button"
      stop.className = "bm-tt__stop"
      stop.dataset.index = String(i)
      stop.innerHTML = `<span class="bm-tt__stop-dot"></span><span class="bm-tt__stop-date">${escapeHtml(era.date)}</span>`
      stop.addEventListener("click", () => this.jumpTo(i))
      this.ribbonEl.appendChild(stop)
    })
  }

  // ---- show / navigate ----------------------------------------------------

  private show(index: number) {
    this.index = clamp(index, 0, this.erasValue.length - 1)
    document.documentElement.style.overflow = "hidden"
    this.overlay!.hidden = false
    // Force a frame so the entrance transition runs.
    requestAnimationFrame(() => {
      this.overlay!.classList.add("is-open")
      this.layout(false)
    })
    document.addEventListener("keydown", this.keyHandler)
  }

  private go(delta: number) {
    const next = clamp(this.index + delta, 0, this.erasValue.length - 1)
    if (next === this.index) return
    this.index = next
    this.layout(true)
  }

  private jumpTo(i: number) {
    const next = clamp(i, 0, this.erasValue.length - 1)
    if (next === this.index) return
    this.index = next
    this.layout(true)
  }

  private layout(animated: boolean) {
    const panelW = this.panelWidth()
    // Center the focused panel: shift the track so panel `index` sits mid-stage.
    const offset = `calc(50% - ${(this.index + 0.5) * panelW}px)`
    this.track.style.transition = animated ? `transform 0.55s ${EASE}` : "none"
    this.track.style.transform = `translateX(${offset})`

    this.panels.forEach((panel, i) => {
      const dist = Math.abs(i - this.index)
      const focused = i === this.index
      panel.style.transition = animated
        ? `opacity 0.55s ${EASE}, filter 0.55s ${EASE}, transform 0.55s ${EASE}`
        : "none"
      panel.style.opacity = focused ? "1" : dist === 1 ? "0.4" : "0.12"
      panel.style.filter = focused ? "none" : "blur(2px)"
      panel.style.transform = focused ? "scale(1)" : "scale(0.82)"
      panel.style.pointerEvents = dist <= 1 ? "auto" : "none"
      panel.classList.toggle("is-focused", focused)
    })

    this.syncVideos()
    this.renderCaption()
    this.syncRibbon()

    // Disable the chevrons at the ends.
    ;(this.overlay!.querySelector('[data-tt="prev"]') as HTMLButtonElement).disabled = this.index === 0
    ;(this.overlay!.querySelector('[data-tt="next"]') as HTMLButtonElement).disabled =
      this.index === this.erasValue.length - 1
  }

  private syncVideos() {
    this.panels.forEach((panel, i) => {
      const v = panel.querySelector("video") as HTMLVideoElement | null
      if (!v) return
      if (i === this.index) {
        if (!v.src && v.dataset.src) v.src = v.dataset.src
        v.play().catch(() => {})
      } else {
        v.pause()
      }
    })
  }

  private renderCaption() {
    const era = this.erasValue[this.index]
    const ordinal = `R${String(this.index + 1).padStart(2, "0")}`
    const span = era.ongoing
      ? `${escapeHtml(era.date)} to today`
      : era.end
        ? `${escapeHtml(era.date)} to ${escapeHtml(era.end)}`
        : escapeHtml(era.date)
    const nameHtml = era.modelSlug
      ? `<a href="/arena/models/${escapeAttr(era.modelSlug)}" class="bm-tt__name">${escapeHtml(era.model)}</a>`
      : `<span class="bm-tt__name">${escapeHtml(era.model)}</span>`
    const bits: string[] = []
    if (era.duration) bits.push(`reigned ${escapeHtml(era.duration)}`)
    if (era.overtook) bits.push(`overtook ${escapeHtml(era.overtook)}`)
    if (era.ongoing) bits.push("holds the crown today")

    const note = era.note
      ? `<div class="bm-tt__note">${ICON_QUOTE}<p>${escapeHtml(era.note)}</p></div>`
      : ""

    this.captionEl.innerHTML = `
      ${note}
      <div class="bm-tt__cap-row">
        <span class="bm-tt__ord">${ordinal}</span>
        ${nameHtml}
        ${era.ongoing ? `<span class="bm-tt__crown">${ICON_CROWN} current</span>` : ""}
      </div>
      <div class="bm-tt__meta"><span class="bm-tt__span">${span}</span>${
        bits.length ? `<span class="bm-tt__sep">/</span>${escapeHtml(bits.join(" / "))}` : ""
      }</div>
    `
  }

  private syncRibbon() {
    Array.from(this.ribbonEl.children).forEach((el, i) => {
      ;(el as HTMLElement).classList.toggle("is-active", i === this.index)
      ;(el as HTMLElement).classList.toggle("is-past", i < this.index)
    })
  }

  private close() {
    this.teardown()
    if (!this.overlay) return
    this.overlay.classList.remove("is-open")
    const el = this.overlay
    window.setTimeout(() => {
      el.hidden = true
    }, 250)
  }

  private teardown() {
    document.removeEventListener("keydown", this.keyHandler)
    document.documentElement.style.overflow = ""
    this.panels.forEach((p) => {
      const v = p.querySelector("video") as HTMLVideoElement | null
      v?.pause()
    })
  }

  private onKey(e: KeyboardEvent) {
    // When the source-zoom layer is open it owns Escape (close just the zoom)
    // and swallows arrow keys so corridor navigation doesn't run underneath it.
    const zoom = this.overlay?.querySelector('[data-tt="zoom"]') as HTMLElement | null
    if (zoom && !zoom.hidden) {
      if (e.key === "Escape") this.closeSourceZoom()
      return
    }
    if (e.key === "Escape") this.close()
    else if (e.key === "ArrowLeft") this.go(-1)
    else if (e.key === "ArrowRight") this.go(1)
  }

  private panelWidth(): number {
    // Keep in sync with .bm-tt__panel flex-basis in TT_CSS:
    // 62vw (capped 760px) desktop, 82vw below the 640px mobile breakpoint.
    if (window.innerWidth <= 640) return window.innerWidth * 0.82
    return Math.min(window.innerWidth * 0.62, 760)
  }

  private injectStyles() {
    if (document.getElementById("bm-tt-styles")) return
    const style = document.createElement("style")
    style.id = "bm-tt-styles"
    style.textContent = TT_CSS
    document.head.appendChild(style)
  }
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v))
}
function escapeHtml(s: string): string {
  const d = document.createElement("div")
  d.textContent = s ?? ""
  return d.innerHTML
}
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, "&quot;")
}

const ICON_X = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>`
const ICON_LEFT = `<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>`
const ICON_RIGHT = `<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>`
const ICON_PLAY = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>`
const ICON_CROWN = `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.52l4.276 3.664a1 1 0 0 0 1.516-.294z"/></svg>`
const ICON_QUOTE = `<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M7 7h4v6a4 4 0 0 1-4 4v-2a2 2 0 0 0 2-2H7zm8 0h4v6a4 4 0 0 1-4 4v-2a2 2 0 0 0 2-2h-2z"/></svg>`

// Scoped to .bm-tt. Dark stage on purpose (a viewing room for the archive), but
// chrome uses color-mix on real daisyUI vars so accents track the theme.
const TT_CSS = `
.bm-tt{position:fixed;inset:0;z-index:200;display:flex;flex-direction:column;align-items:center;justify-content:center;opacity:0;transition:opacity .25s ${EASE}}
.bm-tt.is-open{opacity:1}
.bm-tt[hidden]{display:none}
.bm-tt__scrim{position:absolute;inset:0;background:oklch(0.16 0.02 265 / 0.92);backdrop-filter:blur(8px)}
.bm-tt__close{position:absolute;top:1rem;right:1rem;z-index:3;display:inline-flex;align-items:center;justify-content:center;width:2.5rem;height:2.5rem;border-radius:9999px;color:oklch(0.97 0 0 / 0.7);background:oklch(1 0 0 / 0.06)}
.bm-tt__close:hover{color:#fff;background:oklch(1 0 0 / 0.12)}
.bm-tt__prompt{position:absolute;top:1.25rem;left:50%;transform:translateX(-50%);z-index:2;max-width:min(70ch,80vw);text-align:center;font-size:.8rem;line-height:1.4;color:oklch(0.97 0 0 / 0.62);padding:0 3.5rem}
.bm-tt__stage{position:relative;width:100%;flex:1;display:flex;align-items:center;overflow:hidden}
.bm-tt__track{display:flex;align-items:center;height:100%;will-change:transform}
.bm-tt__panel{flex:0 0 62vw;max-width:760px;height:72vh;margin:0;display:flex;align-items:center;justify-content:center;cursor:pointer;will-change:transform,opacity,filter}
.bm-tt__panel.is-focused{cursor:default}
.bm-tt__media{position:relative;width:100%;height:100%;display:flex;align-items:center;justify-content:center}
.bm-tt__media img,.bm-tt__media video{max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;border-radius:.5rem;box-shadow:0 24px 70px oklch(0 0 0 / 0.55)}
.bm-tt__playbadge{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:3rem;height:3rem;border-radius:9999px;display:flex;align-items:center;justify-content:center;color:#fff;background:oklch(0 0 0 / 0.5);pointer-events:none}
.bm-tt__panel.is-focused .bm-tt__playbadge{display:none}
.bm-tt__nav{position:absolute;top:50%;transform:translateY(-50%);z-index:3;display:inline-flex;align-items:center;justify-content:center;width:3rem;height:3rem;border-radius:9999px;color:oklch(0.97 0 0 / 0.8);background:oklch(1 0 0 / 0.06)}
.bm-tt__nav:hover{background:oklch(1 0 0 / 0.14)}
.bm-tt__nav:disabled{opacity:.25;pointer-events:none}
.bm-tt__nav--prev{left:1rem}
.bm-tt__nav--next{right:1rem}
.bm-tt__caption{position:relative;z-index:2;width:min(48rem,90vw);text-align:center;color:oklch(0.97 0 0 / 0.92);padding:1rem 1rem .35rem}
.bm-tt__cap-row{display:flex;align-items:baseline;justify-content:center;gap:.6rem;flex-wrap:wrap}
.bm-tt__ord{font-family:ui-monospace,monospace;font-size:.7rem;letter-spacing:.1em;color:oklch(0.97 0 0 / 0.4)}
.bm-tt__name{font-size:1.35rem;font-weight:700;letter-spacing:-.01em;color:#fff;text-decoration:none}
a.bm-tt__name:hover{color:var(--color-primary)}
.bm-tt__crown{display:inline-flex;align-items:center;gap:.3rem;font-family:ui-monospace,monospace;font-size:.62rem;text-transform:uppercase;letter-spacing:.18em;color:var(--color-primary)}
.bm-tt__meta{margin-top:.4rem;font-family:ui-monospace,monospace;font-size:.72rem;color:oklch(0.97 0 0 / 0.55)}
.bm-tt__span{color:oklch(0.97 0 0 / 0.8)}
.bm-tt__sep{margin:0 .5rem;color:oklch(0.97 0 0 / 0.3)}
.bm-tt__note{max-width:30rem;margin:0 auto .9rem;display:flex;gap:.55rem;align-items:flex-start;text-align:left;background:color-mix(in oklch, var(--color-primary) 16%, oklch(0.2 0.02 265));border:1px solid color-mix(in oklch, var(--color-primary) 40%, transparent);color:oklch(0.98 0 0 / 0.92);border-radius:.75rem;padding:.7rem .9rem;font-size:.82rem;line-height:1.45}
.bm-tt__note svg{color:var(--color-primary);flex:0 0 auto;margin-top:.1rem}
.bm-tt__note p{margin:0}
.bm-tt__ribbon{position:relative;z-index:2;display:flex;align-items:center;gap:0;max-width:92vw;overflow-x:auto;padding:.75rem 1rem 1.25rem;scrollbar-width:none}
.bm-tt__ribbon::-webkit-scrollbar{display:none}
.bm-tt__stop{position:relative;display:flex;flex-direction:column;align-items:center;gap:.4rem;padding:0 .85rem;background:none;border:none;cursor:pointer;flex:0 0 auto}
.bm-tt__stop::before{content:"";position:absolute;top:.3rem;left:-50%;width:100%;height:2px;background:oklch(1 0 0 / 0.12)}
.bm-tt__stop:first-child::before{display:none}
.bm-tt__stop.is-active::before,.bm-tt__stop.is-past::before{background:color-mix(in oklch, var(--color-primary) 60%, transparent)}
.bm-tt__stop-dot{width:.55rem;height:.55rem;border-radius:9999px;background:oklch(1 0 0 / 0.25);transition:transform .2s ${EASE},background .2s}
.bm-tt__stop.is-past .bm-tt__stop-dot{background:color-mix(in oklch, var(--color-primary) 55%, transparent)}
.bm-tt__stop.is-active .bm-tt__stop-dot{background:var(--color-primary);transform:scale(1.5)}
.bm-tt__stop-date{font-family:ui-monospace,monospace;font-size:.6rem;letter-spacing:.04em;color:oklch(0.97 0 0 / 0.4);white-space:nowrap}
.bm-tt__stop.is-active .bm-tt__stop-date{color:oklch(0.97 0 0 / 0.85)}
.bm-tt__source{position:absolute;left:1rem;bottom:5.5rem;z-index:3;max-width:min(34vw,16rem);background:oklch(0.2 0.02 265 / 0.82);backdrop-filter:blur(6px);border:1px solid oklch(1 0 0 / 0.12);border-radius:.7rem;padding:.6rem}
.bm-tt__source[hidden]{display:none}
.bm-tt__source-label{display:block;font-family:ui-monospace,monospace;font-size:.6rem;text-transform:uppercase;letter-spacing:.14em;color:oklch(0.97 0 0 / 0.72);margin-bottom:.4rem}
.bm-tt__source-label span{color:oklch(0.97 0 0 / 0.42)}
.bm-tt__source-imgs{display:flex;gap:.4rem;flex-wrap:wrap}
.bm-tt__source-thumb{display:block;padding:0;border:none;background:var(--color-base-200);border-radius:.4rem;cursor:zoom-in;line-height:0;transition:transform .15s ${EASE},box-shadow .15s ${EASE}}
.bm-tt__source-thumb:hover{transform:scale(1.04);box-shadow:0 6px 18px oklch(0 0 0 / 0.45)}
.bm-tt__source-thumb:focus-visible{outline:2px solid var(--color-primary);outline-offset:2px}
.bm-tt__source-imgs img{display:block;width:3.5rem;height:3.5rem;object-fit:cover;border-radius:.4rem;border:1px solid oklch(1 0 0 / 0.15)}
.bm-tt__zoom{position:absolute;inset:0;z-index:6;display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .2s ${EASE}}
.bm-tt__zoom.is-open{opacity:1}
.bm-tt__zoom[hidden]{display:none}
.bm-tt__zoom-scrim{position:absolute;inset:0;background:oklch(0.12 0.02 265 / 0.9);backdrop-filter:blur(10px)}
.bm-tt__zoom-close{position:absolute;top:1rem;right:1rem;z-index:2;display:inline-flex;align-items:center;justify-content:center;width:2.5rem;height:2.5rem;border-radius:9999px;color:oklch(0.97 0 0 / 0.8);background:oklch(1 0 0 / 0.08)}
.bm-tt__zoom-close:hover{background:oklch(1 0 0 / 0.16)}
.bm-tt__zoom-figure{position:relative;z-index:1;margin:0;display:flex;flex-direction:column;align-items:center;gap:.7rem;max-width:90vw;max-height:88vh}
.bm-tt__zoom-figure img{max-width:90vw;max-height:80vh;width:auto;height:auto;object-fit:contain;background:var(--color-base-200);border-radius:.6rem;box-shadow:0 30px 80px oklch(0 0 0 / 0.6)}
.bm-tt__zoom-cap{font-family:ui-monospace,monospace;font-size:.62rem;text-transform:uppercase;letter-spacing:.18em;color:oklch(0.97 0 0 / 0.6)}
.bm-tt__panel.is-upscale .bm-tt__media{overflow:hidden;border-radius:.5rem}
.bm-tt__panel.is-upscale.is-focused{cursor:zoom-in}
.bm-tt__panel.is-upscale.is-focused .bm-tt__media img{transition:transform .2s ${EASE}}
@media (max-width:640px){.bm-tt__panel{flex-basis:82vw;height:56vh}.bm-tt__nav{width:2.5rem;height:2.5rem}.bm-tt__name{font-size:1.1rem}.bm-tt__source{bottom:auto;top:3.25rem;left:.5rem;max-width:40vw;padding:.4rem}.bm-tt__source-imgs img{width:2.5rem;height:2.5rem}}
@media (prefers-reduced-motion:reduce){.bm-tt__track,.bm-tt__panel{transition:none !important}}
`
